import { PickingLine } from './types';

/**
 * Strukturvarer: «maks mulig sett»-beregning.
 *
 * Dekning per komponent = det som FAKTISK kan plukkes: allerede plukket + reell saldo på
 * plukklokasjonen (binAvailableQty fra backend), begrenset av bestilt antall per linje.
 * (Reservasjoner kan ikke brukes som mål — lokasjoner med negativ reservasjon reserverer
 * fullt antall uansett saldo.) Antall mulige sett = min over komponentene av
 * floor(dekning / antall pr. sett), begrenset av hovedvarens bestilte antall.
 *
 * Når settene er begrenset, får komponentlinjene structureMaxQty/structureSetsPossible/
 * structureLimitedBy satt — brukt av Plukk-knappen, linje-hint og Aktiv plukklinje-kortet.
 * Speiler backend-sperren i PickingService.EnsureStructurePickAllowed.
 */
export function applyStructureCaps(lines: PickingLine[]): PickingLine[] {
  const headers = lines.filter(l => (l.structureLineType ?? 0) === 1);
  if (headers.length === 0) return lines;

  const caps = new Map<number, { maxQty: number; setsPossible: number; limitedBy?: string }>();

  headers.forEach(header => {
    const comps = lines.filter(l =>
      (l.structureLineType ?? 0) === 2 &&
      l.orderNo === header.orderNo &&
      l.structureParentLineNoERP != null &&
      l.structureParentLineNoERP === header.lineNoERP);
    if (comps.length === 0) return;

    const byComp = new Map<string, { perUnit: number; covered: number; lines: PickingLine[] }>();
    comps.forEach(c => {
      const entry = byComp.get(c.itemNo) ?? { perUnit: 0, covered: 0, lines: [] };
      if (!entry.perUnit && c.structureQtyPerUnit && c.structureQtyPerUnit > 0) {
        entry.perUnit = c.structureQtyPerUnit;
      }
      entry.covered += Math.min(c.qty, c.qtyPicked + Math.max(0, c.binAvailableQty ?? 0));
      entry.lines.push(c);
      byComp.set(c.itemNo, entry);
    });

    let sets: number | null = null;
    let limitedBy: string | undefined;
    byComp.forEach((entry, itemNo) => {
      if (entry.perUnit > 0) {
        const s = Math.floor(entry.covered / entry.perUnit);
        if (sets === null || s < sets) { sets = s; limitedBy = itemNo; }
      }
    });
    if (sets === null) return;

    const setsPossible = Math.min(sets, header.qty);
    if (setsPossible >= header.qty) return; // full dekning — ingen begrensning

    byComp.forEach(entry => {
      if (entry.perUnit <= 0) return;
      let remaining = setsPossible * entry.perUnit;
      entry.lines.filter(l => l.binCode !== '[X]').forEach(l => {
        const lineMax = Math.max(0, Math.min(l.qty, remaining));
        remaining -= lineMax;
        caps.set(l.pListLineNo, { maxQty: lineMax, setsPossible, limitedBy });
      });
    });
  });

  if (caps.size === 0) return lines;
  return lines.map(l => {
    const cap = caps.get(l.pListLineNo);
    return cap
      ? { ...l, structureMaxQty: cap.maxQty, structureSetsPossible: cap.setsPossible, structureLimitedBy: cap.limitedBy }
      : l;
  });
}

/**
 * Har komponentlinjen et reelt saldoavvik i Compass — dvs. lokasjonen dekker ikke linjens
 * bestilte antall? Kun slike linjer skal tilby telle-snarveien: komponenter som har nok
 * på lager (men er begrenset av manko på en ANNEN komponent) har ingenting å telle inn.
 */
export function hasStructureDeficit(line: PickingLine): boolean {
  if ((line.structureLineType ?? 0) !== 2) return false;
  if (!line.binCode || line.binCode === '[X]') return false;
  return line.qtyPicked + Math.max(0, line.binAvailableQty ?? 0) < line.qty;
}

/**
 * Hint-tekst for en begrenset komponentlinje, eller undefined når linjen ikke er begrenset.
 * t = oversettelsesfunksjonen fra useTranslation.
 */
export function getStructureCapNote(
  line: PickingLine,
  t: (key: string, params?: Record<string, string>, fallback?: string) => string
): string | undefined {
  if (line.structureMaxQty === undefined || line.structureMaxQty >= line.qty) {
    return undefined;
  }
  return line.structureLimitedBy && line.structureLimitedBy !== line.itemNo
    ? t('warehouse.modules.picking.structure.maxPick',
        { '0': String(line.structureMaxQty), '1': String(line.structureSetsPossible ?? ''), '2': line.structureLimitedBy },
        'Plukk maks {0} — kun {1} sett mulig (manko på {2})')
    : t('warehouse.modules.picking.structure.maxPickSelf',
        { '0': String(line.structureMaxQty), '1': String(line.structureSetsPossible ?? '') },
        'Plukk maks {0} — kun {1} sett mulig');
}
